import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaPlayCircle, FaBookOpen, FaInfoCircle, FaEye, FaDownload,
    FaCalendarAlt, FaRobot, FaPaperPlane,
    FaFileAlt, FaLanguage, FaComments, FaExclamationTriangle,
    FaTimes, FaChevronDown, FaChevronUp, FaSearch, FaCheck,
    FaHome, FaStar, FaCog, FaArrowLeft
} from 'react-icons/fa';
import SafeLink from './SafeLink';
import PremiumUpgrade from './PremiumUpgrade';
import VideoModal from './VideoModal';
import FilePreviewModal from './FilePreviewModal';
import { resourcesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Resources.css';

// ── Helper function to map resources from BD to card format ────────────────
const mapResourceToCard = (resource) => {
    const videoThumbnails = ['bg-video-1', 'bg-video-2', 'bg-video-3', 'bg-video-4', 'bg-video-5', 'bg-video-6'];
    const guideThumbnails = ['bg-guide-1', 'bg-guide-2', 'bg-guide-3', 'bg-guide-4', 'bg-guide-5', 'bg-guide-6'];
    const infoThumbnails = ['bg-info-1', 'bg-info-2', 'bg-info-3', 'bg-info-4', 'bg-info-5', 'bg-info-6'];

    const categoryName = resource.category?.name?.toLowerCase() || 'videos';
    const isVideo = categoryName.includes('video');
    const isGuide = categoryName.includes('guía') || categoryName.includes('formato') || categoryName.includes('plantilla');
    const isInfo = categoryName.includes('material') || categoryName.includes('informativo');

    let thumbnails = videoThumbnails;
    let category = 'videos';

    if (isGuide) {
        thumbnails = guideThumbnails;
        category = 'guides';
    } else if (isInfo) {
        thumbnails = infoThumbnails;
        category = 'info';
    }

    const thumbnail = thumbnails[resource.id % thumbnails.length];

    return {
        id: resource.id,
        title: resource.name,
        description: resource.description || 'Sin descripción',
        thumbnail,
        duration: isVideo ? `${Math.floor(Math.random() * 15) + 5}:${Math.floor(Math.random() * 60).toString().padStart(2, '0')}` : undefined,
        pages: isGuide ? Math.floor(Math.random() * 30) + 10 : undefined,
        views: Math.floor(Math.random() * 20000) + 1000,
        downloads: isGuide ? Math.floor(Math.random() * 8000) + 1000 : undefined,
        readTime: isInfo ? `${Math.floor(Math.random() * 10) + 5} min` : undefined,
        date: new Date(resource.createdAt).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        }).replace(/\./g, ''),
        level: 'Paciente',
        tags: [resource.category?.name, resource.type?.name].filter(Boolean),
        category,
        fileName: resource.fileName,
        // Almacenar el recurso original completo para VideoModal
        originalResource: resource,
    };
};

// ── MOCK DATA (replaced with real data from BD) ────────────────────────────

// ── Mock AI responses ──────────────────────────────────────────────────────
const AI_RESPONSES = {
    resumir: [
        'Este recurso aborda los aspectos fundamentales de la prevención y el cuidado de la salud cardiovascular. En términos prácticos: el corazón necesita actividad física regular (30 min/día), una dieta baja en sodio y grasas saturadas, y controles periódicos de presión arterial y colesterol. Los factores de riesgo modificables incluyen tabaquismo, sedentarismo y estrés crónico.',
        'El recurso explica cómo interpretar los valores básicos de un análisis sanguíneo. Puntos clave: glucosa en ayunas (70–100 mg/dL = normal), hemoglobina (12–17 g/dL según sexo), colesterol total (<200 mg/dL ideal), y creatinina como indicador renal. Valores fuera de rango no siempre significan enfermedad; siempre requieren interpretación médica.',
        'Este material resume las técnicas basadas en evidencia para el manejo del estrés: respiración diafragmática, meditación mindfulness, actividad física aeróbica y regulación del sueño. La práctica regular de estas técnicas reduce el cortisol, mejora el estado de ánimo y protege la salud cardiovascular a largo plazo.',
    ],
    traducir: [
        'Términos clave en lenguaje simple:\n• **Aterosclerosis** → acumulación de grasa en las arterias que las estrecha\n• **Hipertensión** → presión alta en la sangre que daña las paredes de los vasos\n• **Infarto de miocardio** → ataque al corazón por falta de oxígeno\n• **Dislipidemia** → niveles anormales de grasa (colesterol/triglicéridos) en sangre\n• **Arritmia** → latido irregular del corazón',
        'Glosario del recurso:\n• **Hemograma** → análisis completo de las células de la sangre\n• **Glucemia** → nivel de azúcar (glucosa) en sangre\n• **Leucocitos** → glóbulos blancos que defienden al cuerpo de infecciones\n• **Hematocrito** → porcentaje del volumen de sangre ocupado por glóbulos rojos\n• **Uremia** → nivel de urea en sangre, indica función del riñón',
        'Vocabulario explicado:\n• **Cortisol** → hormona del estrés producida por las glándulas suprarrenales\n• **Neurotransmisor** → mensajero químico entre neuronas del cerebro\n• **Mindfulness** → práctica de atención plena al momento presente\n• **Ansiedad generalizada** → preocupación excesiva y persistente sin causa clara\n• **Serotonina** → hormona que regula el estado de ánimo y el bienbienestar',
    ],
    explicar: [
        'Imagina que tu corazón es una bomba que trabaja sin parar. Para que trabaje bien, necesita: tuberías limpias (arterias sin grasa), combustible de calidad (alimentación sana), mantenimiento regular (ejercicio y chequeos médicos), y descanso adecuado (dormir bien). Cuando alguno de estos falla, es como cuando un motor mal mantenido empieza a fallar. Lo bueno es que todos estos factores podemos controlarlos con hábitos cotidianos.',
        'Piensa en un análisis de sangre como el "tablero de control" de tu cuerpo. Así como el auto tiene luces que avisan cuando algo falla, tus exámenes de laboratorio muestran si tus órganos están funcionando bien. Cuando el médico dice que un valor está "alto" o "bajo", es como una luz de advertencia que indica que algo merece atención, pero no necesariamente que hay una emergencia. El médico es quien puede interpretar todo el cuadro completo.',
        'El estrés es la respuesta de tu cuerpo ante situaciones difíciles, como un sistema de alarma que se activa. En el mundo moderno, esta alarma a veces queda encendida todo el tiempo, lo que agota al cuerpo. Las técnicas de relajación que describe este material son como el interruptor que apaga esa alarma: respirar profundo, hacer ejercicio o descansar bien le dicen al cuerpo que el peligro pasó y que puede calmarse.',
    ],
};

const TABS = [
    { key: 'videos', label: 'Videos', icon: <FaPlayCircle /> },
    { key: 'guides', label: 'Guías', icon: <FaBookOpen /> },
    { key: 'info', label: 'Material Informativo', icon: <FaInfoCircle /> },
];

const LEVEL_COLORS = { Paciente: '#22c55e', General: '#3b82f6', Investigador: '#a855f7' };

// ── Main Component ─────────────────────────────────────────────────────────
const Resources = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const params = new URLSearchParams(location.search);
    const initialTab = params.get('tab') || location.state?.tab || 'videos';
    const [activeTab, setActiveTab] = useState(initialTab);
    const [filter, setFilter] = useState('all');
    const [headerSearch, setHeaderSearch] = useState('');
    const [activeSideNav, setActiveSideNav] = useState('recursos');
    const [currentPage, setCurrentPage] = useState(1);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedItems, setSelectedItems] = useState(new Set());

    const toggleItemSelection = (id) => {
        setSelectedItems(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const itemsPerPage = 6;

    const loadResources = async () => {
        try {
            setLoading(true);
            console.log('Fetching resources from API for REDPATIENT...');
            const response = await resourcesAPI.getByModule('REDPATIENT');
            console.log('API Response:', response.data);
            const mappedResources = response.data.map(mapResourceToCard);
            console.log('Mapped resources:', mappedResources);
            setResources(mappedResources);
        } catch (err) {
            console.error('Error loading resources:', err);
            setResources([]);
        } finally {
            setLoading(false);
        }
    };

    // Load resources from BD on mount
    useEffect(() => {
        loadResources();
    }, []);

    const SIDE_NAV = [
        { key: 'inicio', label: 'Inicio', icon: <FaHome />, action: () => navigate('/') },
        { key: 'recursos', label: 'Recursos', icon: <FaBookOpen />, action: () => { } },
        { key: 'descargas', label: 'Mis Descargas', icon: <FaDownload />, action: () => { } },
        { key: 'favoritos', label: 'Favoritos', icon: <FaStar />, action: () => { } },
        { key: 'config', label: 'Configuración', icon: <FaCog />, action: () => { } },
    ];

    const items = resources.filter(r => r.category === activeTab) || [];
    const levels = ['all', ...new Set(items.map(i => i.level))];
    const filtered = (() => {
        let r = filter === 'all' ? items : items.filter(i => i.level === filter);
        if (headerSearch.trim()) {
            const q = headerSearch.toLowerCase();
            r = r.filter(i => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
        }
        return r;
    })();

    // Pagination logic
    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const startIdx = (currentPage - 1) * itemsPerPage;
    const paginatedItems = filtered.slice(startIdx, startIdx + itemsPerPage);

    const handleTabChange = (key) => {
        setActiveTab(key);
        setFilter('all');
        setCurrentPage(1);
    };

    if (loading && resources.length === 0) {
        return <div className="loading-spinner">Cargando recursos...</div>;
    }

    return (
        <>
            {/* ── HEADER ── */}
            <header className="res-header">
                <div className="res-header-inner">
                    <div className="res-header-left">
                        {user && (
                            <button className="res-back-btn" onClick={() => navigate(-1)}>
                                <FaArrowLeft /> Regresar
                            </button>
                        )}
                        <SafeLink to="/" className="res-header-logo">
                            <img src="/logoecseia.png" alt="Logo" className="res-header-logo-img" />
                            <span className="res-header-logo-eco">Eco</span>
                            <span className="res-header-logo-rest">ScienceInnova</span>
                        </SafeLink>
                    </div>
                    <div className="res-header-right">
                        <div className="res-header-search-wrap">
                            <FaSearch className="res-header-search-icon" />
                            <input
                                className="res-header-search-input"
                                placeholder="Buscar recursos..."
                                value={headerSearch}
                                onChange={e => setHeaderSearch(e.target.value)}
                            />
                        </div>
                        {!user && (
                            <SafeLink to="/login" className="res-login-btn">Iniciar Sesión</SafeLink>
                        )}
                    </div>
                </div>
            </header>

            <div className="resources-page">
                <div className="resources-main-layout">

                    {/* ── LEFT SIDEBAR ── */}
                    <aside className="res-sidebar">
                        <p className="res-side-section-label">MENÚ PRINCIPAL</p>
                        <nav className="res-side-nav">
                            {SIDE_NAV.map(item => (
                                <button
                                    key={item.key}
                                    className={`res-side-nav-item ${activeSideNav === item.key ? 'active' : ''}`}
                                    onClick={() => { setActiveSideNav(item.key); item.action(); }}
                                >
                                    {item.icon} {item.label}
                                </button>
                            ))}
                        </nav>

                        <div className="res-storage-card">
                            <p className="res-storage-label">Espacio de Almacenamiento</p>
                            <div className="res-storage-bar-bg">
                                <div className="res-storage-bar-fill" style={{ width: '85%' }} />
                            </div>
                            <p className="res-storage-text">8.5 GB de 10 GB utilizados</p>
                        </div>

                        <div className="res-sidebar-footer">
                            <img src="/logoecseia.png" alt="Logo" className="res-sf-logo" />
                            <span className="res-sf-text">EcoScienceInnova © 2024</span>
                        </div>
                    </aside>

                    {/* ── MAIN CONTENT ── */}
                    <div className="resources-content">

                        {/* Hero */}
                        <div className="resources-hero">
                            <h1 className="resources-hero-title">
                                Biblioteca de <span className="text-orange">Recursos</span>
                            </h1>
                            <p className="resources-hero-subtitle">
                                Accede a contenido educativo desarrollado por especialistas para pacientes, aspirantes e investigadores.
                            </p>
                        </div>

                        {/* Tabs bar */}
                        <div className="resources-tabs-bar">
                            <div className="resources-tabs">
                                {TABS.map(tab => (
                                    <button
                                        key={tab.key}
                                        className={`resources-tab ${activeTab === tab.key ? 'active' : ''}`}
                                        onClick={() => handleTabChange(tab.key)}
                                    >
                                        <span className="tab-icon">{tab.icon}</span>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                            <div className="resources-filters">
                                {levels.map(lvl => (
                                    <button
                                        key={lvl}
                                        className={`filter-pill ${filter === lvl ? 'active' : ''}`}
                                        onClick={() => setFilter(lvl)}
                                    >
                                        {lvl === 'all' ? 'Todos' : lvl}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Cards + AI panel */}
                        <div className="resources-body">
                            <div className="resources-grid-section">
                                <p className="resources-count">{filtered.length} recursos disponibles</p>
                                <div className="resources-card-grid">
                                    {paginatedItems.slice(0, 3).map(item => (
                                        <ResourceCard
                                            key={item.id}
                                            item={item}
                                            type={activeTab}
                                            onVideoClick={setSelectedVideo}
                                            onFileClick={setSelectedFile}
                                            isSelected={selectedItems.has(item.id)}
                                            onToggleSelect={toggleItemSelection}
                                        />
                                    ))}
                                </div>

                                {totalPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            className="pagination-btn"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            ← Anterior
                                        </button>
                                        <div className="pagination-info">
                                            Página {currentPage} de {totalPages}
                                        </div>
                                        <button
                                            className="pagination-btn"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            Siguiente →
                                        </button>
                                    </div>
                                )}
                            </div>
                            <AiAssistantPanel
                                selectedResources={resources.filter(r => selectedItems.has(r.id))}
                                onClearSelection={() => setSelectedItems(new Set())}
                            />
                        </div>

                    </div>{/* /resources-content */}
                </div>{/* /resources-main-layout */}
            </div>

            {/* Video Modal */}
            <VideoModal resource={selectedVideo} isOpen={!!selectedVideo} onClose={() => setSelectedVideo(null)} />

            {/* File Preview Modal */}
            <FilePreviewModal resource={selectedFile} isOpen={!!selectedFile} onClose={() => setSelectedFile(null)} />
        </>
    );
};

// ── AI Assistant Panel ─────────────────────────────────────────────────────
const AI_ACTIONS = [
    { key: 'resumir', label: 'Resumir', icon: <FaFileAlt />, hint: 'Resumen del recurso seleccionado' },
    { key: 'traducir', label: 'Traducir términos', icon: <FaLanguage />, hint: 'Glosario en lenguaje simple' },
    { key: 'explicar', label: 'Explicar en ciudadano', icon: <FaComments />, hint: 'Explicación sin tecnicismos' },
];

const AiAssistantPanel = ({ selectedResources = [], onClearSelection }) => {
    const [question, setQuestion] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [lastActionKey, setLastActionKey] = useState(null);
    const chatEndRef = useRef(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const simulateResponse = (type, customQ) => {
        setLoading(true);
        setLastActionKey(type);
        const pool = AI_RESPONSES[type];
        const text = pool[Math.floor(Math.random() * pool.length)];
        const userMsg = customQ || AI_ACTIONS.find(a => a.key === type)?.label || type;

        setMessages(prev => [...prev, { role: 'user', text: userMsg, action: type }]);

        // Simulate streaming delay
        setTimeout(() => {
            setMessages(prev => [...prev, { role: 'ai', text, action: type }]);
            setLoading(false);
        }, 1200);
    };

    const handleSend = () => {
        if (!question.trim()) return;
        const q = question.trim();
        setQuestion('');
        setLastActionKey(null);
        setMessages(prev => [...prev, { role: 'user', text: q }]);
        setLoading(true);
        setTimeout(() => {
            setMessages(prev => [...prev, {
                role: 'ai',
                text: 'Entiendo tu consulta. Esta información es de carácter educativo, no diagnóstico. Te recomiendo siempre consultar con un profesional de salud para interpretar resultados o síntomas personales. Basándonos en los recursos disponibles: ' + q.slice(0, 30) + '... es un tema que abordamos en nuestros materiales de salud cardiovascular, metabolismo y bienestar general.'
            }]);
            setLoading(false);
        }, 1400);
    };

    return (
        <aside className={`ai-panel ${collapsed ? 'ai-panel--collapsed' : ''}`}>
            {/* Panel header */}
            <div className="ai-panel-header">
                <div className="ai-panel-title">
                    <div className="ai-status-dot" />
                    <FaRobot className="ai-robot-icon" />
                    <div>
                        <span className="ai-panel-name">Asistente IA de Salud</span>
                        <span className="ai-online-label">En línea</span>
                    </div>
                </div>
                <button className="ai-collapse-btn" onClick={() => setCollapsed(c => !c)}>
                    {collapsed ? <FaChevronDown /> : <FaChevronUp />}
                </button>
            </div>

            {!collapsed && (
                <>
                    {/* Disclaimer */}
                    <div className="ai-disclaimer">
                        <FaExclamationTriangle className="disclaimer-icon" />
                        <p>Este asistente <strong>resume, traduce y explica</strong> en lenguaje ciudadano. <em>No emite diagnósticos ni reemplaza la consulta médica.</em></p>
                    </div>

                    {/* Quick action buttons */}
                    <div className="ai-quick-actions">
                        {/* Recursos seleccionados */}
                        {selectedResources.length > 0 && (
                            <div className="ai-selected-resources">
                                <p className="ai-selected-label">
                                    {selectedResources.length} recurso{selectedResources.length !== 1 ? 's' : ''} seleccionado{selectedResources.length !== 1 ? 's' : ''}
                                </p>
                                <div className="ai-selected-list">
                                    {selectedResources.map(r => (
                                        <div key={r.id} className="ai-selected-item">{r.title}</div>
                                    ))}
                                </div>
                                <button className="ai-clear-selection-btn" onClick={onClearSelection}>
                                    <FaTimes /> Limpiar selección
                                </button>
                            </div>
                        )}

                        <p className="ai-quick-label">Acciones rápidas</p>
                        <div className="ai-actions-grid">
                            {AI_ACTIONS.map(action => (
                                <button
                                    key={action.key}
                                    className={`ai-action-btn ${lastActionKey === action.key ? 'active' : ''}`}
                                    onClick={() => simulateResponse(action.key)}
                                    disabled={loading}
                                >
                                    <span className="ai-action-icon">{action.icon}</span>
                                    <span>{action.label}</span>
                                    {lastActionKey === action.key && <FaCheck className="ai-check" />}
                                </button>
                            ))}
                        </div>

                        {/* Selected action indicator */}
                        {lastActionKey && (
                            <div className="selected-action">
                                <FaCheck className="selected-check" />
                                <span className="selected-text">
                                    {AI_ACTIONS.find(a => a.key === lastActionKey)?.icon} {AI_ACTIONS.find(a => a.key === lastActionKey)?.label}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Chat messages */}
                    {messages.length > 0 && (
                        <div className="ai-chat-log">
                            {messages.map((msg, i) => {
                                const actionLabel = msg.action ? AI_ACTIONS.find(a => a.key === msg.action)?.label : null;
                                return (
                                    <div key={i} className={`ai-msg ai-msg--${msg.role}`}>
                                        {msg.role === 'ai' && <FaRobot className="ai-msg-avatar" />}
                                        <div className="ai-msg-bubble">
                                            {msg.role === 'ai' && msg.action && (
                                                <div className="ai-action-label">📄 {actionLabel}</div>
                                            )}
                                            {msg.text.split('\n').map((line, j) => (
                                                <p key={j} dangerouslySetInnerHTML={{
                                                    __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                            {loading && (
                                <div className="ai-msg ai-msg--ai">
                                    <FaRobot className="ai-msg-avatar" />
                                    <div className="ai-msg-bubble ai-typing">
                                        <span /><span /><span />
                                    </div>
                                </div>
                            )}
                            <div ref={chatEndRef} />
                        </div>
                    )}

                    {/* Input */}
                    <div className="ai-input-area">
                        <input
                            className="ai-input"
                            type="text"
                            placeholder="Haz una pregunta sobre los recursos..."
                            value={question}
                            onChange={e => setQuestion(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSend()}
                            disabled={loading}
                        />
                        <button
                            className="ai-send-btn"
                            onClick={handleSend}
                            disabled={loading || !question.trim()}
                        >
                            <FaPaperPlane />
                        </button>
                    </div>

                    {/* Clear button */}
                    {messages.length > 0 && (
                        <button className="ai-clear-btn" onClick={() => { setMessages([]); setLastActionKey(null); }}>
                            <FaTimes /> Limpiar conversación
                        </button>
                    )}
                </>
            )}
        </aside>
    );
};

// ── Resource Card ──────────────────────────────────────────────────────────
const ResourceCard = ({ item, type, onVideoClick, onFileClick, isSelected = false, onToggleSelect }) => {
    const bgClass = `thumb-${item.thumbnail}`;
    const levelColor = LEVEL_COLORS[item.level] || '#FF6600';

    const handleCardClick = () => {
        if (type === 'videos' && onVideoClick) {
            const videoResource = {
                ...item,
                ...(item.originalResource && {
                    name: item.originalResource.name,
                    type: item.originalResource.type,
                    category: item.originalResource.category
                })
            };
            onVideoClick(videoResource);
        } else if (type !== 'videos' && onFileClick) {
            // Pasar el item mapeado + datos originales para detección de tipo
            onFileClick({
                ...item,
                title: item.title,
                desc: item.description,
                originalResource: item.originalResource,
            });
        }
    };

    const handleSelectClick = (e) => {
        e.stopPropagation();
        if (onToggleSelect) onToggleSelect(item.id);
    };

    return (
        <div
            className={`res-card${isSelected ? ' res-card--selected' : ''}`}
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
        >
            <div className={`res-card-thumb ${bgClass}`}>
                {/* Círculo de selección estilo Google Photos */}
                <button
                    className={`res-select-circle${isSelected ? ' res-select-circle--active' : ''}`}
                    onClick={handleSelectClick}
                    title={isSelected ? 'Deseleccionar' : 'Seleccionar para IA'}
                    aria-label={isSelected ? 'Deseleccionar recurso' : 'Seleccionar recurso'}
                >
                    {isSelected && <FaCheck className="res-select-check" />}
                </button>

                {/* Overlay de selección */}
                {isSelected && <div className="res-select-overlay" />}

                {type === 'videos' && <div className="thumb-play-btn"><FaPlayCircle /></div>}
                {type === 'guides' && <div className="thumb-guide-icon"><FaBookOpen /></div>}
                {type === 'info' && <div className="thumb-info-icon"><FaInfoCircle /></div>}

                <span className="thumb-badge" style={{ color: levelColor, borderColor: levelColor }}>
                    {item.level}
                </span>
                {type === 'videos' && item.duration && <span className="thumb-duration">{item.duration}</span>}
                {type === 'guides' && item.pages && <span className="thumb-duration">{item.pages} pág.</span>}
                {type === 'info' && item.readTime && <span className="thumb-duration">{item.readTime} lectura</span>}
            </div>
            <div className="res-card-body">
                <h3 className="res-card-title">{item.title}</h3>
                <p className="res-card-desc">{item.description}</p>
                <div className="res-card-meta">
                    <span><FaCalendarAlt /> {item.date}</span>
                    {type === 'videos' && <span><FaEye /> {item.views.toLocaleString()}</span>}
                    {type === 'guides' && <span><FaDownload /> {item.downloads.toLocaleString()}</span>}
                    {type === 'info' && <span><FaEye /> {item.views.toLocaleString()}</span>}
                </div>
                <button className="res-card-btn" onClick={handleCardClick}>
                    {type === 'videos' ? 'Reproducir video' : 'Ver recurso'}
                </button>
            </div>
        </div>
    );
};

export default Resources;
